import { Flex, Page, Text } from '@/src/ui-components';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm, UseFormProps } from 'react-hook-form';
import { useNetwork } from '@/src/contexts';
import { getNetworkTokenConfig } from '@/src/constants';
import {
  ActivityAndOrders,
  Balances,
  OraclePrices,
  OrderBook,
  Parameters,
  PriceChart,
} from './components';
import { TradePanel } from './components/trade-panel/trade-panel';
import { TradingFormValues } from './trading.types';
import { TokenId } from '@/src/models';

function TradeContent() {
  const { network } = useNetwork();

  const defaultValues: UseFormProps<TradingFormValues>['defaultValues'] = useMemo(
    () => {
      // Get trading pair configuration from token_config.json
      const networkConfig = getNetworkTokenConfig(network);
      const firstBaseTokenId = networkConfig?.tradingPairs.baseTokenIds[0];
      const firstQuoteTokenId = networkConfig?.tradingPairs.quoteTokenIds[0];

      return {
        baseTokenId: (firstBaseTokenId || 'xrp-mainnet') as TokenId,
        quoteTokenId: (firstQuoteTokenId || 'rlusd-mainnet') as TokenId,
      };
    },
    [network]
  );

  const form = useForm<TradingFormValues>({
    defaultValues,
  });
  const navigate = useNavigate();

  // Reset form when network changes
  useEffect(() => {
    form.reset(defaultValues);
  }, [network, form, defaultValues]);

  const arePairsEqual = form.getValues('baseTokenId') === form.getValues('quoteTokenId');

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-2 h-full">
        {/* Row 1: Parameters (full width) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
          <div className="lg:col-span-4">
            <Parameters />
          </div>
        </div>

        {/* Row 2: OrderBook (1 col) + MarketActivity+OpenOrders (2 cols) + Balances+Oracle+TradePanel (1 col) */}
        {arePairsEqual ? (
          <div className="flex-1 min-h-0">
            <Flex justify="center" className="h-full bg-orange-100 p-4 rounded-lg">
              <Text>Select a different quote and base token</Text>
            </Flex>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-2 flex-1 min-h-0">
            <div className="lg:col-span-1 min-h-0 flex">
              <OrderBook />
            </div>
            <div className="lg:col-span-2 min-h-0 flex">
              <Flex direction="col" gap={2} className="flex-1">
                <div className="flex-1 min-h-0 flex">
                  <PriceChart />
                </div>
                <div className="flex-1 min-h-0 flex">
                  <ActivityAndOrders />
                </div>
              </Flex>
            </div>
            <div className="lg:col-span-1 min-h-0 flex">
              <Flex direction="col" gap={2} className="flex-1">
                <div className="min-h-0 flex">
                  <OraclePrices onNavigateToManage={() => navigate('/oracles')} />
                </div>
                <div className="min-h-0 flex">
                  <Balances />
                </div>
                <div className="flex-1 min-h-0 flex">
                  <TradePanel />
                </div>
              </Flex>
            </div>
          </div>
        )}
      </div>
    </FormProvider>
  );
}

export default function TradePage() {
  return (
    <Page>
      <Page.Body>
        <TradeContent />
      </Page.Body>
    </Page>
  );
}
